/**
 * 認証ストア（Zustand）。
 * Cognito Hosted UI + PKCE での認証フローを管理する（D3 §5）。
 */
import { create } from 'zustand';
import { env } from '@/lib/env';
import type { CurrentUser, UserRole } from '@/types/user';

interface AuthState {
  initializing: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  currentUser: CurrentUser | null;

  initialize: () => Promise<void>;
  setTokens: (tokens: {
    access_token: string;
    id_token: string;
    refresh_token: string;
  }) => void;
  updateTokens: (tokens: {
    access_token: string;
    id_token: string;
  }) => void;
  clear: () => void;
}

function decodeUserFromAccessToken(token: string): CurrentUser | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const paddedPayload = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    const decoded = JSON.parse(atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/')));
    const role = (decoded['custom:role'] ?? (
      decoded['custom:is_admin'] === 'true'
        ? 'admin'
        : decoded['custom:is_feedbacker'] === 'true'
          ? 'feedbacker'
          : 'coach'
    )) as UserRole;
    return {
      id: decoded['custom:user_id'] ?? '',
      cognito_sub: decoded.sub ?? '',
      email: decoded.email ?? '',
      name: decoded.name ?? decoded.email ?? '',
      is_feedbacker: decoded['custom:is_feedbacker'] === 'true',
      is_admin: decoded['custom:is_admin'] === 'true',
      role,
    };
  } catch {
    return null;
  }
}

function loadLocalE2EUser(): CurrentUser | null {
  if (env.VITE_E2E_AUTH_MODE !== 'local') return null;
  try {
    const raw = sessionStorage.getItem('e2e_current_user') ?? localStorage.getItem('e2e_current_user');
    if (!raw) return null;
    const user = JSON.parse(raw) as CurrentUser;
    return user.id ? user : null;
  } catch {
    return null;
  }
}

function recordLastLogin(accessToken: string) {
  if (env.VITE_UI_PREVIEW || env.VITE_E2E_AUTH_MODE === 'local') return;
  void fetch(`${env.VITE_API_BASE_URL}/auth/last-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  }).catch((error) => {
    console.error('[auth] failed to record last login', error);
  });
}

export const useAuthStore = create<AuthState>((set) => ({
  initializing: true,
  isAuthenticated: false,
  accessToken: null,
  idToken: null,
  refreshToken: null,
  currentUser: null,

  initialize: async () => {
    if (env.VITE_UI_PREVIEW) {
      set({
        accessToken: 'ui-preview-token',
        idToken: 'ui-preview-id-token',
        refreshToken: null,
        currentUser: {
          id: '00000000-0000-4000-a000-000000000001',
          cognito_sub: 'ui-preview-sub',
          email: 'preview-admin@test.local',
          name: 'Preview Admin',
          is_feedbacker: true,
          is_admin: true,
          role: 'admin',
        },
        isAuthenticated: true,
        initializing: false,
      });
      return;
    }

    const token = sessionStorage.getItem('access_token') ?? localStorage.getItem('access_token');
    const idToken = sessionStorage.getItem('id_token') ?? localStorage.getItem('id_token');
    const refreshToken = sessionStorage.getItem('refresh_token') ?? localStorage.getItem('refresh_token');
    if (token) {
      const user = loadLocalE2EUser() ?? decodeUserFromAccessToken(token);
      if (user && user.id) {
        set({
          accessToken: token,
          idToken,
          refreshToken,
          currentUser: user,
          isAuthenticated: true,
          initializing: false,
        });
        return;
      }
      sessionStorage.clear();
    }
    set({ initializing: false });
  },

  setTokens: (tokens) => {
    sessionStorage.setItem('access_token', tokens.access_token);
    sessionStorage.setItem('id_token', tokens.id_token);
    sessionStorage.setItem('refresh_token', tokens.refresh_token);
    const user = decodeUserFromAccessToken(tokens.access_token);
    if (user?.id) recordLastLogin(tokens.access_token);
    set({
      accessToken: tokens.access_token,
      idToken: tokens.id_token,
      refreshToken: tokens.refresh_token,
      currentUser: user,
      isAuthenticated: !!user && !!user.id,
    });
  },

  updateTokens: (tokens) => {
    sessionStorage.setItem('access_token', tokens.access_token);
    sessionStorage.setItem('id_token', tokens.id_token);
    const user = decodeUserFromAccessToken(tokens.access_token);
    set({
      accessToken: tokens.access_token,
      idToken: tokens.id_token,
      currentUser: user,
      isAuthenticated: !!user && !!user.id,
    });
  },

  clear: () => {
    sessionStorage.clear();
    set({
      accessToken: null,
      idToken: null,
      refreshToken: null,
      currentUser: null,
      isAuthenticated: false,
    });
  },
}));
